/**
 * NewProjectWizard Component
 * 3단계 위저드 형식의 새 프로젝트 생성 다이얼로그
 *
 * Step 1: 기본 정보 (Title, Description, Tags)
 * Step 2: Project Scope 선택
 * Step 3: Partition 설정
 */

import React, { useState, useCallback } from 'react';
import { DialogTitle, DialogContent, DialogActions, Button, Box, Alert, CircularProgress } from '@mui/material';
import { Dialog } from '@/shared/ui/overlays';
import { ArrowBack, ArrowForward } from '@mui/icons-material';

import StepIndicator from './StepIndicator';
import Step1BasicInfo from './Step1BasicInfo';
import Step2ScopeSelect from './Step2ScopeSelect';
import Step3PartitionSetup from './Step3PartitionSetup';
import Step4MarsConfig from './Step4MarsConfig';
import type { WizardStep, WizardFormData } from './types';
import { INITIAL_WIZARD_DATA, getWizardSteps } from './types';
import { createError, stepError, type WizardRuleContext } from './wizardValidation';
import type { NewProjectFormData } from '@/types/project';

interface NewProjectWizardProps {
  open: boolean;
  onClose: () => void;
  onCreated?: (projectId: string) => void;
  loading?: boolean;
  error?: string | null;
  onCreate: (data: NewProjectFormData) => Promise<string | null>;
  /** 중복 차단용 기존 프로젝트 이름 목록 — 정규화 비교(규칙은 namePolicy) (#282) */
  existingTitles?: readonly string[];
}

/** 오류 알림 한 줄의 높이 — 비어 있어도 이 높이를 지켜 단계 내용의 자리가 고정된다 */
const ALERT_SLOT_HEIGHT = '3rem';

const NewProjectWizard: React.FC<NewProjectWizardProps> = ({
  open,
  onClose,
  onCreated,
  loading = false,
  error,
  onCreate,
  existingTitles = [],
}) => {
  const [currentStep, setCurrentStep] = useState<WizardStep>(1);
  const [formData, setFormData] = useState<WizardFormData>(INITIAL_WIZARD_DATA);
  const [localError, setLocalError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  // MARS 파티션 존재 여부 → 동적 Step 4
  const hasMarsPartition = formData.partitions.some(
    (p) => p.analysisCode === 'MARS'
  );
  const lastStep: WizardStep = hasMarsPartition ? 4 : 3;
  const wizardSteps = getWizardSteps(hasMarsPartition);

  // 폼 데이터 업데이트
  const handleFormChange = useCallback((updates: Partial<WizardFormData>) => {
    setFormData((prev) => {
      const next = { ...prev, ...updates };

      // Step 4에 있는데 MARS 파티션이 모두 제거된 경우 Step 3으로 되돌림
      const stillHasMars = next.partitions.some(
        (p) => p.analysisCode === 'MARS'
      );
      if (!stillHasMars) {
        setCurrentStep((step) => (step === 4 ? 3 : step) as WizardStep);
      }

      return next;
    });
    setLocalError(null);
  }, []);

  // 단계 검증은 규칙 표(wizardValidation) 하나를 구동한다 — NEXT 는 현재 단계, CREATE 는 마지막 단계까지
  const ruleContext: WizardRuleContext = { existingTitles };

  // 다음 스텝으로 이동
  const handleNext = () => {
    const nextStep = (currentStep + 1) as WizardStep;
    const validationError = stepError(currentStep, formData, ruleContext);

    if (validationError) {
      setLocalError(validationError);
      return;
    }

    setLocalError(null);
    setCurrentStep(nextStep);
  };

  // 이전 스텝으로 이동
  const handleBack = () => {
    setLocalError(null);
    setCurrentStep((prev) => (prev - 1) as WizardStep);
  };

  // 스텝 클릭으로 이동 (완료된 스텝만)
  const handleStepClick = (step: WizardStep) => {
    if (step < currentStep) {
      setLocalError(null);
      setCurrentStep(step);
    }
  };

  // 프로젝트 생성
  const handleCreate = async () => {
    const validationError = createError(lastStep, formData, ruleContext);
    if (validationError) {
      setLocalError(validationError);
      return;
    }

    setIsCreating(true);
    try {
      const projectData: NewProjectFormData = {
        title: formData.title,
        description: formData.description,
        scope: formData.scope,
        partitions: formData.partitions,
      };

      const projectId = await onCreate(projectData);
      if (projectId) {
        onCreated?.(projectId);
        handleClose();
      }
    } catch (err) {
      setLocalError('프로젝트 생성에 실패했습니다.');
    } finally {
      setIsCreating(false);
    }
  };

  // 다이얼로그 닫기
  const handleClose = () => {
    setFormData(INITIAL_WIZARD_DATA);
    setCurrentStep(1);
    setLocalError(null);
    onClose();
  };

  const displayError = localError || error;

  // 현재 스텝 렌더링
  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return <Step1BasicInfo data={formData} onChange={handleFormChange} />;
      case 2:
        return <Step2ScopeSelect data={formData} onChange={handleFormChange} />;
      case 3:
        return <Step3PartitionSetup data={formData} onChange={handleFormChange} />;
      case 4:
        return <Step4MarsConfig data={formData} onChange={handleFormChange} />;
      default:
        return null;
    }
  };

  // 버튼 상태
  const isLastStep = currentStep === lastStep;
  const isFirstStep = currentStep === 1;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      PaperProps={{
        // 틀은 단계와 무관하게 고정한다 — 단계를 넘길 때 창이 뛰지 않는다. 크기는 계통도 단계(2·3·4)가 정한다:
        // 계통 이름 층만 1.5배로 그리는 그림(DiagramPane) — 이름 22개(DIAGRAM_NAME_LABELS)의 글자 원본은 13.65~19.95px(1678px 보기 상자)이라
        // ~706px 폭에서 8.6(CORE)~12.6(RPV·PZR·CCS)px, 중앙값 11.9px(생성본 systemDiagram.generated 에서 센 값). 오른쪽 열은 22rem.
        sx: { width: 'min(78rem, 96vw)', height: 'min(52rem, 90vh)' },
      }}
    >
      <DialogTitle sx={{ fontWeight: 600, pb: 0 }}>New Project</DialogTitle>

      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          pt: 0,
          pb: 1,
        }}
      >
        {/* Step Indicator */}
        <StepIndicator currentStep={currentStep} steps={wizardSteps} onStepClick={handleStepClick} />

        {/* 오류 알림 — 자리를 항상 비워 둔다(고정 높이): 알림이 생길 때 아래 그림·폼이 밀리지 않는다 */}
        <Box sx={{ height: ALERT_SLOT_HEIGHT, mb: 1, mx: 1, flexShrink: 0 }}>
          {displayError && (
            <Alert severity="error" title={displayError} /* 한 줄로 줄인 문장의 전문은 네이티브 툴팁으로 — 칸 높이를 지키기 위한 선택 */ sx={{ py: 0, '& .MuiAlert-message': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }}>
              {displayError}
            </Alert>
          )}
        </Box>

        {/* Step Content */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto',
            px: 1,
          }}
        >
          {renderCurrentStep()}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
        {/* Back Button */}
        {!isFirstStep && (
          <Button onClick={handleBack} disabled={loading || isCreating} startIcon={<ArrowBack />}>
            Back
          </Button>
        )}

        <Box sx={{ flex: 1 }} />

        {/* Cancel Button */}
        <Button onClick={handleClose} disabled={loading || isCreating}>
          Cancel
        </Button>

        {/* Next / Create Button */}
        {isLastStep ? (
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={loading || isCreating || formData.partitions.length === 0}
            startIcon={isCreating ? <CircularProgress size={16} /> : null}
          >
            {isCreating ? 'Creating…' : 'Create'}
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={loading || isCreating}
            endIcon={<ArrowForward />}
          >
            Next
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default NewProjectWizard;
